import React, { useCallback, useMemo } from 'react';
import SubPage, { PageActions } from '#/common/components/SubPage';
import WorkOrders from '../components/WorkOrders';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import useDocumentTitle from '#/common/hooks/useDocumentTitle';
import If from '#/common/components/If';
import { Navigate, useSearchParams } from 'react-router-dom';
import WOTemplate from '../../wo-template/components/WOTemplates';
import useWorkOrderPropsCounts from '../hooks/useWorkOrderPropsCountsQuery';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import { PageAction } from '#/app/providers/PageProvider';

export type WorkOrdersTab = 'list' | 'templates';

export const defaultTab: WorkOrdersTab = 'list';

const tabs = [
  {
    key: 'list',
    text: 'Work Orders',
    extra: {
      newPath: '/work-orders/new',
      newLabel: 'Work Order',
    },
  },
  {
    key: 'templates',
    text: 'Templates',
    extra: {
      newPath: '/work-orders/templates/new',
      newLabel: 'Template',
    },
  },
] satisfies Tab<WorkOrdersTab>[];

export interface WorkOrdersProps {
  readonly tab: WorkOrdersTab;
}

const WorkOrdersPage: React.FC<WorkOrdersProps> = ({ tab }) => {
  const { user } = useSession();
  const isRoleTechnician = !!user && user.role === UserRole.technician && user.id;
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId') || undefined;

  const selectedTab = tabs.find((t) => t.key === tab);
  const title = useMemo(() => {
    if (selectedTab?.key === 'list') {
      return isRoleTechnician ? `My ${selectedTab?.text}` : selectedTab?.text;
    }

    return `Work Order ${selectedTab?.text}`;
  }, [isRoleTechnician, selectedTab]);

  useDocumentTitle(title);

  const { workOrders = 0, templates = 0 } = useWorkOrderPropsCounts();

  const getPath = useCallback(
    (tab: string) => {
      if (tab === 'list' && isRoleTechnician) {
        const filters = ['assignedToId', '=', user?.id];
        const params = new URLSearchParams();
        params.set('filters', JSON.stringify(filters));
        return `list?${params.toString()}`;
      }

      return tab;
    },
    [user, isRoleTechnician],
  );

  const workOrdersTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'list') {
          return {
            ...t,
            count: workOrders,
            path: getPath(t.key),
          };
        }
        if (t.key === 'templates') {
          return {
            ...t,
            count: templates,
            path: getPath(t.key),
          };
        }
        return t;
      }),
    [isRoleTechnician, workOrders, templates, getPath],
  ) as Tab[];

  const invalidTab = tabs.every((t) => t.key !== tab);
  if (invalidTab) {
    const redirectTo = `../${defaultTab}`;
    return <Navigate to={redirectTo} replace />;
  }

  const newPath =
    selectedTab?.key && `${selectedTab.extra.newPath}${assetId ? `?assetId=${assetId}` : ''}`;

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: 'new-wo-item',
        alwaysShow: true,
        type: 'button',
        props: {
          type: 'submit',
          icon: 'add',
          label: `Create New ${selectedTab?.extra.newLabel}`,
          responsive: true,
          path: newPath,
        },
      },
    ],
    [newPath, selectedTab],
  );

  return (
    <SubPage title={title} tabs={workOrdersTabs} selectedTab={tab} type="list">
      <PageActions actions={actions} />
      <If condition={tab === 'list'}>
        <WorkOrders hideNewAction />
      </If>
      <If condition={tab === 'templates'}>
        <WOTemplate />
      </If>
    </SubPage>
  );
};

export default React.memo(WorkOrdersPage);
